<template>
    <div class="fedex-direct-onboarding-panel">
        <div class="mb-3">
            <h5 class="mb-1">FedEx Direct Onboarding</h5>
            <p class="text-muted mb-0">
                Enable FedEx Direct shipping for this pharmacy. Complete each step below —
                once all steps are complete, FedEx Direct will be available for this store's
                shipments.
            </p>
        </div>

        <!-- Step 1: EULA acceptance -->
        <section class="onboarding-step">
            <h6 class="step-title">Step 1 — FedEx EULA</h6>
            <FedExEulaAcceptance :store-id="storeId" @accepted="onEulaAccepted" />
        </section>

        <!-- Step 2: Customer Registration & MFA -->
        <section class="onboarding-step mt-4">
            <h6 class="step-title">Step 2 — Customer Registration &amp; MFA</h6>
            <b-alert v-if="gateError" show variant="danger" class="mb-2">
                <strong>Could not check EULA acceptance status.</strong>
                <div class="small mt-1">{{ gateError }}</div>
            </b-alert>
            <FedExRegistrationWizard v-if="eulaAccepted"
                                     :store-id="storeId"
                                     :eula-accepted="eulaAccepted"
                                     @status-changed="onRegistrationChanged" />
            <div v-else-if="!gateError" class="text-muted">
                <i class="fa fa-lock mr-1"></i>
                Accept the EULA above to begin customer registration.
            </div>
        </section>

        <!-- Required by FedEx Integrator program: this disclaimer must appear on every screen
             of the GUI where FedEx logos, trademarks, service marks, or product names are present.
             Verbatim per AGD Section 3c, minimum 8pt font. -->
        <p class="fedex-disclaimer mt-3 mb-0 text-muted">
            The FedEx service marks are owned by Federal Express Corporation and are used by permission.
        </p>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import FedExEulaAcceptance from "@/components/Store/FedExEulaAcceptance.vue";
import FedExRegistrationWizard from "@/components/Store/FedExRegistrationWizard.vue";
import { FedExEulaService } from "@/services/FedExEulaService";

@Component({
    name: "FedExDirectOnboardingPanel",
    components: { FedExEulaAcceptance, FedExRegistrationWizard }
})
export default class FedExDirectOnboardingPanel extends Vue {
    @Prop({ required: true }) protected storeId!: number;

    protected eulaAccepted: boolean = false;
    protected gateError: string | null = null;

    async created() {
        await this.refreshEulaState();
    }

    @Watch("storeId")
    async onStoreChange() {
        this.eulaAccepted = false;
        await this.refreshEulaState();
    }

    async onEulaAccepted() {
        this.eulaAccepted = true;
        this.gateError = null;
        this.$emit("status-changed");
    }

    async onRegistrationChanged() {
        this.$emit("status-changed");
    }

    private async refreshEulaState() {
        if (!this.storeId) return;
        try {
            // Use the EULA-specific endpoint here — it has the smallest dependency footprint
            // (just the EULA repo + version const) so the Step 2 gate doesn't get falsely held
            // shut by an unrelated failure in the registration endpoint's dependency chain.
            const status = await FedExEulaService.getStatus(this.storeId);
            this.eulaAccepted = status.hasCurrentAcceptance;
            this.gateError = null;
        } catch (err: any) {
            // Surface the failure so we can actually see why the gate is stuck closed instead
            // of silently swallowing it.
            this.eulaAccepted = false;
            this.gateError = err?.response?.data ?? err?.message ?? "Failed to load EULA status.";
            // eslint-disable-next-line no-console
            console.error("FedExDirectOnboardingPanel: EULA gate check failed", err);
        }
    }
}
</script>

<style scoped>
.onboarding-step {
    padding: 1rem;
    border: 1px solid var(--border-color, #dee2e6);
    border-radius: 4px;
}

.step-title {
    margin-bottom: 0.75rem;
    font-weight: 600;
}

/* AGD requirement: at least 8pt font. Bootstrap base is 16px (12pt), so anything
   non-zero satisfies the floor. Using small italic for visual de-emphasis without
   shrinking below the threshold. */
.fedex-disclaimer {
    font-size: 0.75rem;
    font-style: italic;
    text-align: center;
}
</style>
